// ══════════════════════════════════════════════════════════════════
//  ED CRM - Sino de notificações (barra superior)
// ══════════════════════════════════════════════════════════════════
//  Caixa de entrada dos lembretes de atividade e reunião (Config/102-104).
//  Mora na Navigation, então é global: qualquer tela recebe.
//
//  Camadas de entrega, da mais barata para a mais cara:
//    1. este sino + badge (realtime, sempre ligado);
//    2. notificação nativa do navegador (aba aberta, opt-in por permissão);
//    3. e-mail/WhatsApp pelo dispatcher do banco (CRM fechado).
//
//  Ações direto no item: abrir o lead, concluir a atividade e adiar 1 h —
//  reusando completeActivity/snoozeActivity, o mesmo caminho da Central.
// ══════════════════════════════════════════════════════════════════

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Clock, X } from 'lucide-react';
import type { NotificationEntry } from '@/types/notification';
import { getNotificationKindMeta, completeActivity, snoozeActivity } from '@app';
import { useNotifications } from '@hooks/useNotifications';
import { toast } from '@utils';
import DynIcon from '@components/ui/DynIcon';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const fmtWhen = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1)   return 'agora';
  if (diffMin < 60)  return `há ${diffMin} min`;
  if (diffMin < 1440) return `há ${Math.floor(diffMin / 60)} h`;
  return d.toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [alert, setAlert] = useState<NotificationEntry | null>(null);

  const openLead = useCallback((n: NotificationEntry) => {
    if (!n.lead_id) return;
    setOpen(false);
    navigate(`/leads/${n.lead_id}`);
  }, [navigate]);

  const {
    notifications, unreadCount, loading, markRead, markAllRead, dismiss,
  } = useNotifications({ onNotificationClick: openLead, onNewNotification: setAlert });

  const closeAlert = () => {
    if (alert && !alert.read_at) void markRead([alert.id]);
    setAlert(null);
  };

  const handleOpenItem = async (n: NotificationEntry) => {
    if (!n.read_at) await markRead([n.id]);
    openLead(n);
  };

  const handleComplete = async (n: NotificationEntry) => {
    if (n.entity_type !== 'activity' || !n.entity_id) return;
    setBusyId(n.id);
    try {
      await completeActivity(n.entity_id);
      // O trigger apaga as notificações pendentes da atividade; esta já foi
      // entregue, então sai da caixa por conta própria.
      await dismiss(n.id);
      setAlert(current => current?.id === n.id ? null : current);
      toast.success('Atividade concluída.');
    } catch {
      toast.error('Não foi possível concluir.');
    } finally {
      setBusyId(null);
    }
  };

  const handleSnooze = async (n: NotificationEntry) => {
    if (n.entity_type !== 'activity' || !n.entity_id) return;
    setBusyId(n.id);
    try {
      await snoozeActivity(n.entity_id, 1);
      await dismiss(n.id);
      setAlert(current => current?.id === n.id ? null : current);
      toast.success('Adiada em 1 hora.');
    } catch {
      toast.error('Não foi possível adiar.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Notificações"
          aria-label={unreadCount > 0 ? `Notificações (${unreadCount} não lidas)` : 'Notificações'}
          className="relative h-9 w-[38px] flex items-center justify-center rounded-lg font-sans cursor-pointer border border-white/[0.14] bg-white/[0.06] text-white/[0.72] transition-colors hover:text-white hover:bg-white/[0.13] data-[state=open]:text-white data-[state=open]:bg-white/[0.13] [&>svg]:w-[15px] [&>svg]:h-[15px]"
        >
          <Bell />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-danger-solid text-white text-3xs font-extrabold inline-flex items-center justify-center border border-navy">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[360px] p-0 max-[480px]:w-[calc(100vw-24px)]">
        <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-border">
          <span className="text-sm font-bold text-text">Notificações</span>
          {unreadCount > 0 && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-blue text-xs font-semibold"
              onClick={() => markAllRead()}
            >
              Marcar todas como lidas
            </Button>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {loading ? (
            <div className="px-3.5 py-7 text-center text-xs text-text3">Carregando…</div>
          ) : notifications.length === 0 ? (
            <div className="px-3.5 py-7 text-center text-xs text-text3 leading-[1.6]">
              Nada por aqui. Lembretes de atividades e reuniões aparecem neste sino.
            </div>
          ) : (
            notifications.map(n => {
              const meta = getNotificationKindMeta(n.kind);
              const isActivity = n.entity_type === 'activity';
              const busy = busyId === n.id;
              return (
                <div
                  key={n.id}
                  className={`flex gap-2.5 px-3.5 py-2.5 border-b border-border last:border-b-0 ${n.read_at ? 'bg-white' : 'bg-blue-l/40'}`}
                >
                  <span
                    className="w-[26px] h-[26px] shrink-0 rounded-full inline-flex items-center justify-center mt-0.5"
                    style={{ background: `${meta.color}1A`, color: meta.color }}
                  >
                    <DynIcon name={meta.icon} size={13} />
                  </span>

                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      className="block w-full text-left bg-transparent border-0 p-0 cursor-pointer"
                      onClick={() => handleOpenItem(n)}
                      disabled={!n.lead_id}
                    >
                      <span className="block text-sm font-bold text-text truncate">{n.title}</span>
                      {n.body && <span className="block text-xs text-text2 leading-[1.5] mt-0.5">{n.body}</span>}
                      <span className="block text-2xs text-text3 mt-1">
                        {meta.label} · {fmtWhen(n.delivered_at || n.created_at)}
                      </span>
                    </button>

                    {isActivity && (
                      <div className="flex gap-1.5 mt-1.5">
                        <Button
                          type="button" variant="outline" size="sm"
                          className="h-7 px-2 text-2xs"
                          disabled={busy}
                          onClick={() => handleComplete(n)}
                        >
                          <Check /> Concluir
                        </Button>
                        <Button
                          type="button" variant="outline" size="sm"
                          className="h-7 px-2 text-2xs"
                          disabled={busy}
                          onClick={() => handleSnooze(n)}
                        >
                          <Clock /> Adiar 1 h
                        </Button>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    title="Descartar"
                    aria-label="Descartar notificação"
                    className="shrink-0 h-5 w-5 inline-flex items-center justify-center rounded-md text-text3 bg-transparent border-0 cursor-pointer hover:text-danger-solid hover:bg-danger-bg"
                    onClick={() => dismiss(n.id)}
                  >
                    <X size={13} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>

    <Dialog open={!!alert} onOpenChange={(nextOpen) => { if (!nextOpen) closeAlert(); }}>
      <DialogContent className="max-w-[440px]">
        {alert && (
          <>
            <DialogHeader className="text-left">
              <DialogTitle className="flex items-center gap-2 text-navy">
                <Bell size={18} className="text-blue" /> {alert.title}
              </DialogTitle>
              <DialogDescription className="pt-2 text-text2">
                {alert.body || getNotificationKindMeta(alert.kind).label}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-row gap-2 sm:justify-start">
              {alert.entity_type === 'activity' && (
                <>
                  <Button disabled={busyId === alert.id} onClick={() => handleComplete(alert)}>
                    <Check /> Concluir
                  </Button>
                  <Button variant="outline" disabled={busyId === alert.id} onClick={() => handleSnooze(alert)}>
                    <Clock /> Adiar 1 h
                  </Button>
                </>
              )}
              {alert.lead_id && (
                <Button variant="outline" onClick={() => { closeAlert(); openLead(alert); }}>
                  Abrir lead
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default NotificationBell;
